import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import googleDriveService from '../services/googleDrive';
import { useAuth } from './Auth';

const DynamicIndex = () => {
  const { user } = useAuth();
  const [technicalProcesses, setTechnicalProcesses] = useState([]);
  const [operationalProcedures, setOperationalProcedures] = useState([]);
  const [onboardingDocumentation, setOnboardingDocumentation] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    if (user) {
      loadAllSections();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAllSections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [technical, operational, onboarding, knowledge] = await Promise.all([
        googleDriveService.getTechnicalProcesses(),
        googleDriveService.getOperationalProcedures(),
        googleDriveService.getOnboardingDocumentation(),
        googleDriveService.getKnowledgeBase()
      ]);
      
      setTechnicalProcesses(technical);
      setOperationalProcedures(operational);
      setOnboardingDocumentation(onboarding);
      setKnowledgeBase(knowledge);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Error loading sections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const convertToUrlSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const renderDocCard = (title, description, items, baseRoute, cardId) => (
    <div className="doc-card" id={cardId}>
      <div className="card-header">
        <div className="card-title-area">
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      </div>
      
      <div className="card-content">
        {!user ? (
          <div className="auth-prompt">
            <p>Sign in to access {title} from Google Drive</p>
          </div>
        ) : loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading from Google Drive...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>Error loading: {error}</p>
            <button onClick={loadAllSections} className="retry-button">
              Retry
            </button>
          </div>
        ) : (
          <div className="process-list">
            {items.length === 0 ? (
              <p>No folders found in {title}</p>
            ) : (
              items.map((item) => (
                <Link 
                  key={item.id}
                  to={`/${baseRoute}/${convertToUrlSlug(item.name)}`}
                  className="process-link"
                  data-folder-name={item.name}
                  data-folder-id={item.id}
                >
                  <span className="process-name">{item.name}</span>
                  <span className="process-arrow">→</span>
                </Link>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="documentation-portal">
      <div className="documentation-grid">
        {/* Technical Processes */}
        {renderDocCard(
          "Technical Processes",
          "Development workflows, deployment procedures, and technical guidelines",
          technicalProcesses,
          "technical-processes",
          "technical-processes"
        )}

        {/* Operational Procedures */}
        {renderDocCard(
          "Operational Procedures", 
          "Daily operations, project management, and communication standards",
          operationalProcedures,
          "operational-procedures",
          "operational-procedures"
        )}

        {/* Onboarding Documentation */}
        {renderDocCard(
          "Onboarding Documentation",
          "New employee setup, training materials, and team introductions", 
          onboardingDocumentation,
          "onboarding-documentation",
          "onboarding-documentation"
        )}

        {/* Knowledge Base */}
        {renderDocCard(
          "Knowledge Base",
          "Troubleshooting guides, best practices, and lessons learned",
          knowledgeBase,
          "knowledge-base", 
          "knowledge-base"
        )}
      </div>

        <hr />

        <section className="help-section">
        <h2>Getting Help</h2>
        <ul>
          <li><strong>Technical Issues</strong>: Contact the DevOps team</li>
          <li><strong>Process Questions</strong>: Check with your team lead</li>
          <li><strong>Documentation Updates</strong>: Submit a pull request or contact the documentation team</li>
        </ul>
      </section>
    </div>
  );
};

export default DynamicIndex;